import React from 'react';
import { Col, FormGroup } from 'reactstrap';
import { Control, FieldValues, Path, UseFormTrigger, useFieldArray } from 'react-hook-form';
import { FormCommonProps } from './FormCommon';
import { FormTextControl } from './FormText';
import { FormLabel } from './FormLabel';
import BoldButton from '../BoldButton';

interface FormEmailListProps<FormType extends FieldValues> extends Omit<FormCommonProps<FormType>, 'name'> {
  control: Control<FormType>,
  trigger: UseFormTrigger<FormType>,
  disabled: boolean,
  name: Path<FormType>,
  labelSpace: number,
  inputSpace: number,
  max: number,
}

export const toEmailFields = (emails?: string[]): { email: string }[] => {
  const fields = emails?.map(email => ({ email })) ?? [];
  return fields.length === 0 ? [{ email: '' }] : fields;
};

export const fromEmailFields = (fields: { email: string }[]): string[] =>
  fields.map(f => f.email).filter(e => !!e);

export function FormEmailList(props: FormEmailListProps<any>) {
  const { name, labelSpace, inputSpace, max } = props;

  const emailAddresses = useFieldArray({ control: props.control, name });

  const validateUniqueEmail = (value: string, formValues: FieldValues) => {
    if (value && (formValues[name] as { email: string }[]).map(f => f.email).filter(e => e === value).length > 1) {
      return 'Email already exists';
    }
    return true;
  };

  const removeEmail = (index: number) => {
    emailAddresses.remove(index);
    props.trigger(name);
  };

  return <>
    {
      emailAddresses.fields.map((field, index) => {
        const isFirst = index === 0;
        const showX = emailAddresses.fields.length > 1;
        return <FormGroup row key={field.id}>
          {isFirst &&
            <Col xs={{ size: labelSpace }}>
              <FormLabel displayName='Email' name={`${name}.${index}.email`} />
            </Col>
          }
          <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
            <FormTextControl
              name={`${name}.${index}.email`}
              setValue={props.setValue}
              register={props.register}
              errors={props.errors}
              displayName='Email address'
              options={{
                onChange: () => props.trigger(name),
                validate: validateUniqueEmail,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email Address is invalid'
                }
              }} />
          </Col>
          {
            showX && !props.disabled &&
            <Col xs="1" className="pe-0">
              <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
            </Col>
          }
        </FormGroup>
      })}
    <FormGroup row>
      <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
        <BoldButton
          className='col-auto btn-ghost-primary'
          disabled={emailAddresses.fields.length >= max}
          onClick={() => emailAddresses.append({ email: '' })}>
          Add Email
        </BoldButton>
      </Col>
    </FormGroup>
  </>;
}
